import { authFetch, BASE_BACKEND_URL } from '../../auth'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import Pagination from '../../layout/Pagination'
import usePagination from '../../layout/usePagination'
import LoadingScreen from '../../components/LoadingScreen'
import GoogleIcon from '../../components/icon/GoogleIcon'
import ReassignUserModal from './components/modal/ReassignUserModal'
import StoreRelationsTable from './components/table/StoreRelationsTable'
import StoreRelationsFilters from './filters/StoreRelationsFilters'
import { DEFAULT_PER_PAGE } from '../../constants/constant'
import { hasAnyActiveFilter } from '../PurchaseOrder/helpers/filterUtils'
import {
  buildPrincipalResolutionContext,
  buildStoreRelationCurrentReassignUser,
  buildStoreRelationReassignUserOptions,
  buildStoreRelationStatusPayload,
  getStoreRelationReassignDescription
} from './helpers/storeRelationPrincipalUtils'
import {
  canStoreRelationAssignPrincipal,
  hasStoreRelationPendingPrincipalAssignment,
  hasStoreRelationPrincipalConflict,
  isStoreRelationClientInactive,
  resolveStoreRelationOverallStatus
} from './helpers/storeRelationStatusUtils'
import useFetchStoreOverview from './hooks/useFetchStoreOverview'
import useStoreRelationSalesmanSearch from './hooks/useStoreRelationSalesmanSearch'
import {
  DEFAULT_FILTER_STATE,
  DEFAULT_PRINCIPAL_CONFLICT_MESSAGE,
  STORE_RELATIONS_ACTION_LABELS,
  STORE_RELATIONS_ASSIGN_USER_DETAILS_MESSAGE,
  STORE_RELATIONS_ASSIGN_USER_DETAILS_MESSAGE_SUFFIX,
  STORE_RELATIONS_ASSIGN_USER_FLOW_MESSAGE,
  STORE_RELATIONS_ERROR_PAGE_ALERT_MESSAGE_PREFIX,
  STORE_RELATIONS_ERROR_PAGE_ALERT_MESSAGE_SUFFIX,
  STORE_RELATIONS_ERROR_PAGE_ALERT_STATUS,
  STORE_RELATIONS_INACTIVE_CLIENT_MESSAGE,
  STORE_RELATIONS_NO_PRINCIPAL_CANDIDATES_MESSAGE,
  STORE_RELATIONS_ALERT_DURATION
} from './constants/storeConstants'

const StoreRelationsIndex = () => {
  const navigate = useNavigate()
  const alertTimeoutRef = useRef(null)
  const [supplierFilter, setSupplierFilter] = useState(DEFAULT_FILTER_STATE)
  const [buyerFilter, setBuyerFilter] = useState(DEFAULT_FILTER_STATE)
  const [clientFilter, setClientFilter] = useState(DEFAULT_FILTER_STATE)
  const [userFilter, setUserFilter] = useState(DEFAULT_FILTER_STATE)
  const [statusFilter, setStatusFilter] = useState(DEFAULT_FILTER_STATE)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'success',
    action: null
  })
  const [reassignModalState, setReassignModalState] = useState({
    open: false,
    model: null,
    nextUser: null,
    nextUserSearch: ''
  })

  const shouldShowReset = hasAnyActiveFilter({
    allSelectedOptions: new Map([
      ['PROVEEDOR', supplierFilter.values || []],
      ['CADENA', buyerFilter.values || []],
      ['CLIENTE', clientFilter.values || []],
      ['USUARIO', userFilter.values || []],
      ['ESTADO', statusFilter.values || []]
    ]),
    processedFilter: null,
    searchInput
  })

  const handleResetFilters = () => {
    setSupplierFilter(DEFAULT_FILTER_STATE)
    setBuyerFilter(DEFAULT_FILTER_STATE)
    setClientFilter(DEFAULT_FILTER_STATE)
    setUserFilter(DEFAULT_FILTER_STATE)
    setStatusFilter(DEFAULT_FILTER_STATE)
    setSearchInput('')
    setAppliedSearch('')

    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
  }

  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })

  const showAlert = useCallback((type, message, action = null) => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current)
    }

    setAlertState({
      open: true,
      type,
      message,
      action
    })

    alertTimeoutRef.current = setTimeout(() => {
      setAlertState((prev) => ({
        ...prev,
        open: false,
        action: null
      }))
    }, STORE_RELATIONS_ALERT_DURATION)
  }, [])

  const buildFilterQuery = useCallback((filter, queryParamName) => {
    if (!filter || filter.mode === 'all' || !filter.values?.length) {
      return ''
    }

    return `&${queryParamName}=${encodeURIComponent(filter.values.join(','))}`
  }, [])

  const {
    data,
    loading,
    tableLoading,
    supplierOptions,
    buyerOptions,
    clientOptions,
    userOptions,
    statusOptions,
    fetchData,
    fetchFilters
  } = useFetchStoreOverview({
    pagination,
    setPagination,
    supplierFilter,
    buyerFilter,
    clientFilter,
    userFilter,
    statusFilter,
    appliedSearch,
    buildFilterQuery,
    onError: showAlert
  })

  const getPrincipalConflictMessage = useCallback((model) => {
    return model?.flow?.message || DEFAULT_PRINCIPAL_CONFLICT_MESSAGE
  }, [])
  const { options: searchedReassignUserOptions, loading: reassignUsersLoading } =
    useStoreRelationSalesmanSearch(reassignModalState.nextUserSearch)

  const currentReassignUser = useMemo(() => {
    return reassignModalState.open
      ? buildStoreRelationCurrentReassignUser(reassignModalState.model)
      : null
  }, [reassignModalState.model, reassignModalState.open])

  const reassignUserOptions = useMemo(() => {
    return buildStoreRelationReassignUserOptions(
      reassignModalState.model,
      searchedReassignUserOptions
    )
  }, [reassignModalState.model, searchedReassignUserOptions])

  const reassignModalDescription = useMemo(() => {
    return getStoreRelationReassignDescription(reassignModalState.model)
  }, [reassignModalState.model])
  const currentPageErrorCount = useMemo(() => {
    return data.filter((model) => resolveStoreRelationOverallStatus(model) === 'error').length
  }, [data])
  const currentPageErrorAlertMessage = useMemo(() => {
    if (currentPageErrorCount <= 0) return ''

    const relationLabel = currentPageErrorCount === 1 ? 'relación requiere' : 'relaciones requieren'
    return `${currentPageErrorCount} ${relationLabel} atención:`
  }, [currentPageErrorCount])

  const handleCloseAlert = (_, reason) => {
    if (reason === 'clickaway') return

    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current)
      alertTimeoutRef.current = null
    }

    setAlertState((prev) => ({
      ...prev,
      open: false,
      action: null
    }))
  }

  useEffect(() => {
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current)
      }
    }
  }, [])

  const ensureClientIsActive = useCallback(
    (model) => {
      if (!isStoreRelationClientInactive(model)) return true

      showAlert('error', STORE_RELATIONS_INACTIVE_CLIENT_MESSAGE)
      return false
    },
    [showAlert]
  )

  const sendStoreRelationPatchRequest = useCallback(async (endpoint, payload) => {
    const response = await authFetch(`${BASE_BACKEND_URL}/poc/store_relations_overview/${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    })

        if (!response) return null

    const responseBody = await response.json().catch(() => ({}))

    if (!response.ok) {
      const message =
        responseBody?.flow?.message || responseBody?.errors?.[0] || `HTTP error ${response.status}`
      const error = new Error(message)
      error.status = response.status
      error.responseBody = responseBody
      throw error
    }

    return responseBody
  }, [])

  const handleSuspend = async (model) => {
    const clientName = model?.client?.name

    if (!ensureClientIsActive(model)) return

    if (hasStoreRelationPrincipalConflict(model)) {
      showPrincipalConflictAlert(model)
      return
    }

    try {
      const response = await sendStoreRelationPatchRequest(
        'suspend_store_salesman',
        buildStoreRelationStatusPayload(model)
      )
      if (response === null) return

      showAlert('success', `Relación de ${clientName} suspendida correctamente.`)

      await Promise.all([fetchData(), fetchFilters()])
    } catch (error) {
      console.error('handleSuspend error', error)
      showAlert('error', error?.message || `No fue posible suspender la relación de ${clientName}.`)
    }
  }

  const handleEdit = (model) => {
    const relationId = model?.client_principal?.id

    if (!ensureClientIsActive(model)) return

    if (!relationId) {
      showAlert('error', 'No fue posible abrir la edición. Falta el id de la relación.')
      return
    }

    navigate(`/store_relations/${relationId}/edit`, {
      state: {
        storeRelationData: model,
        returnTo: '/store_relations'
      }
    })
  }

  const handleAssignPrincipal = useCallback(
    (model) => {
      if (!ensureClientIsActive(model)) return

      if (!canStoreRelationAssignPrincipal(model)) {
        showAlert('error', STORE_RELATIONS_NO_PRINCIPAL_CANDIDATES_MESSAGE)
        return
      }

      navigate('/store_relations/new', {
        state: {
          storeRelationData: model,
          lockBaseInfo: true,
          flowAction: 'assign_principal',
          flowMessage: STORE_RELATIONS_ASSIGN_USER_FLOW_MESSAGE,
          pageTitle: `Asignar usuario: ${model?.client?.db_ref || model?.client?.name || 'N/A'}`,
          returnTo: '/store_relations'
        }
      })
    },
    [ensureClientIsActive, navigate, showAlert]
  )

  const handleOpenReassignPrincipal = (model) => {
    if (!ensureClientIsActive(model)) return

    setReassignModalState({
      open: true,
      model,
      nextUser: null,
      nextUserSearch: ''
    })
  }

  const showPrincipalConflictAlert = (model) => {
    showAlert('error', getPrincipalConflictMessage(model), {
      label: STORE_RELATIONS_ACTION_LABELS.REASSIGN_USER,
      suffix: STORE_RELATIONS_ASSIGN_USER_DETAILS_MESSAGE_SUFFIX,
      handler: () => handleOpenReassignPrincipal(model)
    })
  }

  const handleOpenDetails = (model) => {
    const relationId = model?.client_principal?.id

    if (hasStoreRelationPrincipalConflict(model)) {
      showPrincipalConflictAlert(model)
      return
    }

    if (hasStoreRelationPendingPrincipalAssignment(model) || !relationId) {
      if (!ensureClientIsActive(model)) {
        return
      }

      if (!canStoreRelationAssignPrincipal(model)) {
        showAlert('error', STORE_RELATIONS_NO_PRINCIPAL_CANDIDATES_MESSAGE)
        return
      }

      showAlert('warning', STORE_RELATIONS_ASSIGN_USER_DETAILS_MESSAGE, {
        label: STORE_RELATIONS_ACTION_LABELS.ASSIGN_USER,
        suffix: STORE_RELATIONS_ASSIGN_USER_DETAILS_MESSAGE_SUFFIX,
        handler: () => handleAssignPrincipal(model)
      })
      return
    }

    window.open(`/store_relations/${relationId}`, '_blank', 'noopener,noreferrer')
  }

  const handleCloseReassignPrincipal = () => {
    setReassignModalState({
      open: false,
      model: null,
      nextUser: null,
      nextUserSearch: ''
    })
  }

  const handleConfirmReassignPrincipal = async () => {
    const model = reassignModalState.model
    const nextUser = reassignModalState.nextUser

    if (!model || !nextUser) return
    if (!ensureClientIsActive(model)) return

    const principalResolutionContext = buildPrincipalResolutionContext(model, nextUser)

    try {
      const response = await sendStoreRelationPatchRequest('resolve_principal_conflict', {
        ...buildStoreRelationStatusPayload(model),
        principal_salesman_db_ref: nextUser?.db_ref || null,
        principal_resolution_context: principalResolutionContext
      })
      if (response === null) return

      handleCloseReassignPrincipal()
      showAlert(
        'success',
        `Conflicto de usuario principal resuelto correctamente para ${model?.client?.name || 'el cliente seleccionado'}.`
      )
      await Promise.all([fetchData(), fetchFilters()])
    } catch (error) {
      console.error('handleConfirmReassignPrincipal error', error)
      showAlert(
        'error',
        error?.message || 'No fue posible resolver el conflicto de usuario principal.'
      )
    }
  }

  const handleReactivate = async (model) => {
    const clientName = model?.client?.name

    if (!ensureClientIsActive(model)) return

    if (hasStoreRelationPrincipalConflict(model)) {
      showPrincipalConflictAlert(model)
      return
    }

    try {
      const response = await sendStoreRelationPatchRequest(
        'reactivate_store_salesman',
        buildStoreRelationStatusPayload(model)
      )
      if (response === null) return

      showAlert('success', `Relación de ${clientName} reactivada correctamente.`)

      await Promise.all([fetchData(), fetchFilters()])
    } catch (error) {
      console.error('handleReactivate error', error)
      showAlert('error', error?.message || `No fue posible reactivar la relación de ${clientName}.`)
    }
  }

  const handleCreateRelation = () => {
    navigate('/store_relations/new')
  }

  const handleClientChange = (nextValue) => {
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setClientFilter(nextValue)
  }

  const handleSupplierChange = (nextValue) => {
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setSupplierFilter(nextValue)
  }

  const handleBuyerChange = (nextValue) => {
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setBuyerFilter(nextValue)
  }

  const handleUserChange = (nextValue) => {
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setUserFilter(nextValue)
  }

  const handleStatusChange = (nextValue) => {
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setStatusFilter(nextValue)
  }

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleSearchEnter = (event) => {
    if (event.key !== 'Enter') return

    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setAppliedSearch(searchInput)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setPagination((prev) => ({
      ...prev,
      current_page: 1
    }))
    setAppliedSearch('')
  }

  if (loading) {
    return <LoadingScreen text="Cargando..." />
  }

  const handleAlertAction = () => {
    const actionHandler = alertState.action?.handler
    handleCloseAlert()
    actionHandler?.()
  }

  return (
    <div>
      {!tableLoading && currentPageErrorAlertMessage && (
        <div className="store-relations-page-alert">
          <Alert severity="warning">
            <div className="store-relations-alert-message">
              <strong>{currentPageErrorAlertMessage}</strong>
              {' '}
              <span>{STORE_RELATIONS_ERROR_PAGE_ALERT_MESSAGE_PREFIX}</span>
              <strong>{STORE_RELATIONS_ERROR_PAGE_ALERT_STATUS}</strong>
              <span>{STORE_RELATIONS_ERROR_PAGE_ALERT_MESSAGE_SUFFIX}</span>
            </div>
          </Alert>
        </div>
      )}

      {alertState.open && (
        <div className="store-relations-page-alert">
          <Alert severity={alertState.type} onClose={handleCloseAlert}>
            <div className="store-relations-alert-message">
              <span>{alertState.message}</span>
              {alertState.action && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="store-relations-alert-action"
                    onClick={handleAlertAction}
                  >
                    "{alertState.action.label}"
                  </button>{' '}
                  <span>{alertState.action.suffix}</span>
                </>
              )}
            </div>
          </Alert>
        </div>
      )}

      <div className="index-header detailed-container store-relations-header">
        <h2 className="mb-20 store-relations-title">Asignar relaciones de cliente con usuarios</h2>
      </div>

      <div className="index-table">
        <div className="show-area">
          <StoreRelationsFilters
            supplierFilter={supplierFilter}
            buyerFilter={buyerFilter}
            clientFilter={clientFilter}
            userFilter={userFilter}
            statusFilter={statusFilter}
            supplierOptions={supplierOptions}
            buyerOptions={buyerOptions}
            clientOptions={clientOptions}
            userOptions={userOptions}
            statusOptions={statusOptions}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            onSearchEnter={handleSearchEnter}
            onClearSearch={handleClearSearch}
            onSupplierChange={handleSupplierChange}
            onBuyerChange={handleBuyerChange}
            onClientChange={handleClientChange}
            onUserChange={handleUserChange}
            onStatusChange={handleStatusChange}
            shouldShowReset={shouldShowReset}
            onReset={handleResetFilters}
          />

          <StoreRelationsTable
            data={data}
            loading={tableLoading}
            onShowDetails={handleOpenDetails}
            onAssignPrincipal={handleAssignPrincipal}
            onReassignPrincipal={handleOpenReassignPrincipal}
            onSuspend={handleSuspend}
            onEdit={handleEdit}
            onReactivate={handleReactivate}
          />
        </div>
      </div>

      <nav className="index-table">
        {pagination.total_pages > 1 && (
          <div onClick={handlePaginationClick}>
            <Pagination
              current_page={pagination.current_page}
              total_pages={pagination.total_pages}
            />
          </div>
        )}
      </nav>

      <ReassignUserModal
        show={reassignModalState.open}
        description={reassignModalDescription}
        currentUser={currentReassignUser}
        nextUser={reassignModalState.nextUser}
        nextUserOptions={reassignUserOptions}
        nextUserSearch={reassignModalState.nextUserSearch}
        loading={reassignUsersLoading}
        onClose={handleCloseReassignPrincipal}
        onConfirm={handleConfirmReassignPrincipal}
        onNextUserChange={(nextUser) => {
          setReassignModalState((prev) => ({
            ...prev,
            nextUser,
            nextUserSearch: nextUser?.label || prev.nextUserSearch
          }))
        }}
        onNextUserSearchChange={(nextUserSearch) => {
          setReassignModalState((prev) => ({
            ...prev,
            nextUserSearch,
            nextUser: null
          }))
        }}
      />
    </div>
  )
}

export default StoreRelationsIndex
